import React, { useContext, useEffect, useState } from 'react'
import { useLazyQuery, gql, NetworkStatus } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import moment from 'moment'
import ChartLine from '../../../../components/charts/ChartLine'
import ChartLineSkeleton from '../../../../components/skeletons/ChartLineSkeleton'
import { ChartLineThemes } from '../../../../components/charts/types/ChartLine'
import { formatCurrency, formatDateCard } from '../../../../utils/Helpers'
import { GET_STATUS_CANCELLED_DELIVERED_CHECKOUT_TRANSLATE } from '../../../../queries/index'
import { DailyMonitoringFiltersContext } from '../../../../context/DailyMonitoringFiltersContext'
import { FilterDailyMonitoring } from '../../../../types/FilterDailyMonitoring'
import { STATUS_CANCELLED, STATUS_DELIVERED, STATUS_CHECKOUT, AGG_STATUS_DELIVERED, AGG_STATUS_CANCELLED, AGG_STATUS_CHECKOUT } from '../../../../types/Order'
import { defaultDateRange, defaultFormatTimeZone } from '../../../../utils/consts'
import './style/icon-style.css'

type BucketStatus = {
  key: string
  doc_count: number
}

type BucketTypes = {
  key_as_string: string,
  key: number,
  doc_count: number,
  cancelled: {
    doc_count : number,
    total : {
      value : number
    }
  },
  delivered: {
    doc_count : number,
    total : {
      value : number
    }
  },
  checkout: {
    doc_count : number,
    total : {
      value : number
    }
  }
}

type Series = {
  name: string
  data: number[]
}

const SalesTotalsHistory = (): JSX.Element => {
  const [dateInterval, setDateInterval] = useState('1d')
  const [calendarInterval, setCalendarInterval] = useState('calendar_interval')

  const GET_DATA = gql`
    query OrdersHistory($filters: JSON) {
      ordersAggs(
        filters: $filters,
        size: 0,
        query: {
          bool: {
            filter: [
              {
                terms: {
                  status_translate_keyword: [
                    "${STATUS_DELIVERED}",
                    "${STATUS_CANCELLED}",
                    "${STATUS_CHECKOUT}"
                  ]
                }
              }
            ]
          }
        },  
        aggs: {
          salesOverTime: {
            date_histogram: {
              field: "created_at",
              ${calendarInterval}: "${dateInterval}",
              time_zone: "-03:00"
            },
            aggs: {
              ${AGG_STATUS_DELIVERED}: {
                filter: {
                  term: {
                    status_translate_keyword: "${STATUS_DELIVERED}"
                  }
                },
                aggs: {
                  total: {
                    sum: {
                      field: "total"
                    }
                  }
                }
              },
              ${AGG_STATUS_CANCELLED}: {
                filter: {
                  term: {
                    status_translate_keyword: "${STATUS_CANCELLED}"
                  }
                },
                aggs: {
                  total: {
                    sum: {
                      field: "total"
                    }
                  }
                }
              },
              ${AGG_STATUS_CHECKOUT}: {
                filter: {
                  term: {
                    status_translate_keyword: "${STATUS_CHECKOUT}"
                  }
                },
                aggs: {
                  total: {
                    sum: {
                      field: "total"
                    }
                  }
                }
              }
            }
          }
        }
      ) {
        aggregations
      }
    }
    `

  const { filters, setFilters } = useContext(DailyMonitoringFiltersContext)
  const categories: Array<string> = []
  const series: Array<Series> = []

  const [getDataStatus, { loading: loadingStatus, error: errorStatus, data: dataStatus }] = useLazyQuery(
    GET_STATUS_CANCELLED_DELIVERED_CHECKOUT_TRANSLATE, {
      notifyOnNetworkStatusChange: true
    }
  )
  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_DATA, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    getDataStatus()
  }, [])

  useEffect(() => {
    if (!dataStatus) return

    let createdAt: FilterDailyMonitoring['dateRange'] = { ...defaultDateRange }

    if (filters.dateRange) {
      createdAt = {
        ...filters.dateRange,
        ...defaultFormatTimeZone
      }
    }

    const a = moment(createdAt.initialDate)
    const b = moment(createdAt.finalDate)
    const difference = b.diff(a, 'day')

    if (difference >= 0 && difference <= 15) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1d')
    } else if (difference >= 16 && difference <= 91) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1w')
    } else if (difference >= 92 && difference <= 182) {
      setCalendarInterval('fixed_interval')
      setDateInterval('15d')
    } else if (difference >= 183 && difference <= 366) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1M')
    } else if (difference > 366) {
      setCalendarInterval('calendar_interval')
      setDateInterval('1q')
    }

    getData({
      variables: { filters: { ...filters, createdAt } }
    })
  }, [dataStatus, filters])

  if (errorStatus || error) return <p>Error :(</p>
  if (loadingStatus || !dataStatus) return <ChartLineSkeleton />
  if (!dataStatus.ordersAggs.aggregations) return <p>No data found</p>

  dataStatus.ordersAggs.aggregations.status.buckets.map((bucket: BucketStatus) => {
    return series.push({ name: bucket.key, data: [] })
  })

  if (networkStatus === NetworkStatus.refetch) return <ChartLineSkeleton />
  if (loading || !data) return <ChartLineSkeleton />
  if (!data.ordersAggs.aggregations.salesOverTime) return <p>No data found</p>

  const { buckets } = data.ordersAggs.aggregations.salesOverTime

  series.map(serie => {
    buckets.map((bucket: BucketTypes) => {
      if (serie.name === STATUS_DELIVERED) {
        serie.data.push(bucket.delivered.total.value)
      } else if (serie.name === STATUS_CANCELLED) {
        serie.data.push(bucket.cancelled.total.value)
      } else if (serie.name === STATUS_CHECKOUT) {
        serie.data.push(bucket.checkout.total.value)
      } else {
        serie.data.push(0)
      }
    })
  })

  buckets.map((bucket: BucketTypes) => {
    categories.push(bucket.key_as_string)
  })

  const options = {
    chart: {
      id: 'orders-total-history',
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: false,
          zoomout: false,
          pan: true,
          reset: false,
          customIcons: [{
            icon: '<svg fill="#6E8192" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" width="20px" height="20px"><path d="M 12 2 A 1 1 0 0 0 11.289062 2.296875 L 1.203125 11.097656 A 0.5 0.5 0 0 0 1 11.5 A 0.5 0.5 0 0 0 1.5 12 L 4 12 L 4 20 C 4 20.552 4.448 21 5 21 L 9 21 C 9.552 21 10 20.552 10 20 L 10 14 L 14 14 L 14 20 C 14 20.552 14.448 21 15 21 L 19 21 C 19.552 21 20 20.552 20 20 L 20 12 L 22.5 12 A 0.5 0.5 0 0 0 23 11.5 A 0.5 0.5 0 0 0 22.796875 11.097656 L 12.716797 2.3027344 A 1 1 0 0 0 12.710938 2.296875 A 1 1 0 0 0 12 2 z"/></svg>',
            index: 2,
            title: 'Reiniciar zoom',
            class: 'custom-icon',
            click: function () {
              const dates = {
                initialDate: window.sessionStorage.getItem('initial_date_picker'),
                finalDate: window.sessionStorage.getItem('final_date_picker')
              }

              if (!dates.initialDate && !dates.finalDate) {
                setFilters({
                  dateRange: {
                    initialDate: moment().clone().subtract(15, 'days').format('YYYY-MM-DD'),
                    finalDate: moment().clone().format('YYYY-MM-DD'),
                    ...defaultFormatTimeZone
                  }
                })
              } else {
                setFilters({
                  dateRange: {
                    ...dates,
                    ...defaultFormatTimeZone
                  }
                })
              }
            }
          }]
        }
      },
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: false,
        zoomedArea: {
          fill: {
            color: '#90CAF9',
            opacity: 0.4
          },
          stroke: {
            color: '#0D47A1',
            opacity: 0.4,
            width: 1
          }
        }
      },
      events: {
        zoomed: function (chartContext: any, { xaxis }: any) {
          const getDataPosition = chartContext?.data?.twoDSeriesX
          const dateFilterIndex = chartContext?.data?.w?.config?.yaxis[0]?.categories
          const closestMin = getDataPosition.reduce(function (prev: number, curr: number) {
            return (Math.abs(curr - xaxis.min) < Math.abs(prev - xaxis.min) ? curr : prev)
          })
          const closestMax = getDataPosition.reduce(function (prev: number, curr: number) {
            return (Math.abs(curr - xaxis.max) < Math.abs(prev - xaxis.max) ? curr : prev)
          })
          const maxPosition = getDataPosition.indexOf(Math.max(closestMax))
          const minPosition = getDataPosition.indexOf(Math.min(closestMin))

          setFilters({
            dateRange: {
              initialDate: moment(dateFilterIndex[minPosition]).clone().format('YYYY-MM-DD'),
              finalDate: moment(dateFilterIndex[maxPosition]).clone().format('YYYY-MM-DD'),
              ...defaultFormatTimeZone
            }
          })
        }
      }
    },
    xaxis: {
      categories,
      type: 'datetime',
      labels: {
        formatter: (value: Date) => {
          return moment(value).format('DD-MM-YYYY')
        }
      }
    },
    yaxis: {
      categories,
      labels: {
        formatter: (value: number) => {
          return formatCurrency(value)
        }
      }
    }
  }

  return <Paper elevation={2}>
    <Typography paragraph style={{ paddingLeft: '20px', paddingTop: '20px' }}>
      Total de vendas ({formatDateCard(filters.dateRange?.initialDate, filters.dateRange?.finalDate)})
      {dateInterval === '1d'
        ? (<Typography variant='caption'> filtrado por dia</Typography>)
        : dateInterval === '1w'
          ? (<Typography variant='caption'> filtrado por semana</Typography>)
          : dateInterval === '15d'
            ? (<Typography variant='caption'> filtrado por quinzena</Typography>)
            : dateInterval === '1M'
              ? (<Typography variant='caption'> filtrado por mês</Typography>)
              : dateInterval === '1q'
                ? (<Typography variant='caption'> filtrado por trimestre</Typography>)
                : ''
      }
    </Typography>
    <ChartLine options={options} series={series} theme={ChartLineThemes.threecolors} height={320} type="area" />
  </Paper>
}

export default SalesTotalsHistory
